import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Heart, Briefcase, Sparkles } from "lucide-react";
import { Helmet } from "react-helmet";
import SchemaMarkup from "@/components/SchemaMarkup";
import { createArticleSchema } from "@/utils/schemaHelpers";

const HoroscopoTauro = () => {
  const articleSchema = createArticleSchema(
    "Tauro: Horóscopo Diario, Características y Compatibilidad con los 12 Signos",
    "Descubre el horóscopo diario, semanal y mensual de Tauro. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 20 de abril y el 20 de mayo.",
    `${window.location.origin}/horoscopo-tauro`
  );

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Horóscopo Tauro",
        "item": `${window.location.origin}/horoscopo-tauro`
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Tauro: Horóscopo Diario, Características y Compatibilidad con los 12 Signos</title>
        <meta name="description" content="Descubre el horóscopo diario, semanal y mensual de Tauro. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 20 de abril y el 20 de mayo." />
        <link rel="canonical" href={`${window.location.origin}/horoscopo-tauro`} />
        <meta property="og:title" content="Tauro: Horóscopo Diario, Características y Compatibilidad" />
        <meta property="og:description" content="Descubre el horóscopo diario, semanal y mensual de Tauro. Predicciones de amor, trabajo, salud y dinero." />
        <meta property="og:url" content={`${window.location.origin}/horoscopo-tauro`} />
        <meta property="og:type" content="article" />
      </Helmet>
      <SchemaMarkup schema={articleSchema} />
      <SchemaMarkup schema={breadcrumbSchema} />
      
      <div className="min-h-screen">
        <Navigation />
        
        <div className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <div className="text-6xl mb-4">♉</div>
              <h1 className="text-3xl font-bold mb-4 text-glow">
                Tauro: Horóscopo Diario, Características y Compatibilidad con los 12 Signos
              </h1>
              <p className="text-xl text-muted-foreground">20 de Abril - 20 de Mayo</p>
            </div>

            {/* Horoscope Predictions */}
            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-primary">Tu Horóscopo Tauro: Predicciones Actualizadas</h2>
              
              <Card className="p-8 gradient-celestial">
                <Tabs defaultValue="hoy" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 mb-8">
                    <TabsTrigger value="hoy">Hoy</TabsTrigger>
                    <TabsTrigger value="semanal">Semanal</TabsTrigger>
                    <TabsTrigger value="mensual">Mensual</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="hoy" className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                        <Heart className="w-6 h-6 text-primary" />
                        Horóscopo de Hoy para Tauro
                      </h3>
                      <p className="text-center text-sm text-muted-foreground mb-6">Número: 6 | Color: Verde Esmeralda</p>
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-lg font-semibold mb-2 text-primary">💖 Amor</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            La influencia de Venus te envuelve en sensualidad y estabilidad. Disfruta de los pequeños placeres con tu pareja; una cena exquisita fortalecerá el vínculo. Valora la lealtad.
                          </p>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold mb-2 text-secondary">💼 Trabajo</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            La persistencia es tu mayor activo hoy. Los asuntos de dinero o inversión se ven favorecidos. Avanza con calma y método, asegurando cada paso con tu sentido práctico.
                          </p>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold mb-2 text-accent">✨ Salud</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            Concéntrate en el bienestar físico a través de la comodidad. Disfruta de un masaje o de alimentos nutritivos. La conexión con la naturaleza te aportará paz.
                          </p>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold mb-2 text-accent">💰 Dinero</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            La estabilidad y la paciencia rinden frutos. Concéntrate en asegurar tus bienes y revisar tus inversiones a largo plazo. Busca la calidad en tus adquisiciones.
                          </p>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="semanal" className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                        <Briefcase className="w-6 h-6 text-secondary" />
                        Horóscopo Semanal: Tu Guía para los Próximos 7 Días
                      </h3>
                      <p className="text-muted-foreground leading-relaxed mb-4">
                        Esta semana trae oportunidades para consolidar tus proyectos y relaciones. Los primeros días favorecen las cuestiones materiales y financieras, mientras que el fin de semana es ideal para el romance y la creatividad. Tu paciencia y perseverancia serán recompensadas.
                      </p>
                      <p className="text-muted-foreground leading-relaxed">
                        Mantén tu firmeza ante cualquier presión externa. Tu capacidad para construir sobre bases sólidas será reconocida por superiores y seres queridos. Dedica tiempo a disfrutar de los placeres sensoriales que tanto te nutren.
                      </p>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="mensual" className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                        <Sparkles className="w-6 h-6 text-accent" />
                        Horóscopo Mensual Tauro: Enfoque y Oportunidades Clave
                      </h3>
                      <p className="text-muted-foreground leading-relaxed mb-4">
                        Este mes marca un período de crecimiento material y emocional. Las primeras semanas favorecen inversiones y decisiones financieras importantes. Venus transita en posición favorable, trayendo armonía a tus relaciones y posibles aumentos de ingresos.
                      </p>
                      <p className="text-muted-foreground leading-relaxed">
                        La segunda mitad del mes es ideal para proyectos creativos y para expresar tu lado artístico. Tu sentido estético y tu buen gusto serán especialmente apreciados. Es momento de construir las estructuras que sostendrán tu futuro con paciencia y dedicación.
                      </p>
                    </div>
                  </TabsContent>
                </Tabs>
              </Card>
            </section>

            {/* Deep Characteristics */}
            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-secondary">Características Profundas del Signo Tauro: El Constructor</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-2xl font-bold mb-4">Los Pilares Astrológicos de Tauro</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-bold text-lg mb-2 text-primary">🌍 Elemento: Tierra</h4>
                    <p className="text-muted-foreground">Enfoque en la estabilidad, lo tangible y lo sensual. Tauro está conectado con el mundo material y físico.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2 text-secondary">🔒 Modalidad: Fijo</h4>
                    <p className="text-muted-foreground">Define la resistencia, la perseverancia y la terquedad. Una vez que toma una decisión, difícilmente cambia de opinión.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2 text-accent">♀️ Planeta Regente: Venus</h4>
                    <p className="text-muted-foreground">Diosa del Amor, la Belleza y el Valor. Rige el placer, los sentidos y la apreciación estética.</p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-celestial">
                <h3 className="text-2xl font-bold mb-4">Perfil de Personalidad Tauro: Fortalezas y Debilidades</h3>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-bold text-lg mb-3 text-primary">✨ Fortalezas</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Lealtad:</strong> Fiel y confiable en todas sus relaciones</li>
                      <li>• <strong>Estabilidad:</strong> Proporciona seguridad y consistencia</li>
                      <li>• <strong>Sentido Práctico:</strong> Excelente para materializar ideas</li>
                      <li>• <strong>Paciencia:</strong> Sabe esperar el momento adecuado</li>
                      <li>• <strong>Sensualidad:</strong> Profunda conexión con los placeres físicos</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-3 text-secondary">⚠️ Debilidades</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Terquedad:</strong> Resistencia extrema al cambio</li>
                      <li>• <strong>Posesividad:</strong> Tendencia a aferrarse a personas y cosas</li>
                      <li>• <strong>Materialismo:</strong> Puede valorar excesivamente lo material</li>
                      <li>• <strong>Lentitud:</strong> Toma tiempo para actuar y decidir</li>
                      <li>• <strong>Indulgencia:</strong> Puede excederse en placeres</li>
                    </ul>
                  </div>
                </div>
                <div className="text-center p-4 bg-primary/10 rounded-lg">
                  <p className="text-xl font-bold text-primary">Mantra Clave: "Yo Tengo"</p>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-2xl font-bold mb-4">Tauro en el Amor: Sensualidad y Compromiso</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  En el amor, Tauro busca estabilidad, seguridad y una conexión física profunda. Venus, su planeta regente, lo convierte en uno de los signos más románticos y sensuales del zodiaco. Valora las demostraciones tangibles de afecto: regalos, cenas, tiempo de calidad y contacto físico.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Una vez comprometido, Tauro es extremadamente leal y devoto. Busca relaciones duraderas donde pueda construir un hogar seguro y confortable. Su naturaleza posesiva puede ser tanto una fortaleza (protección) como un desafío (celos). Necesita una pareja que valore la rutina, la comodidad y los placeres simples de la vida.
                </p>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-2xl font-bold mb-4">Tauro en el Trabajo y Finanzas: Seguridad y Excelencia Material</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Tauro brilla en carreras que requieren paciencia, perseverancia y sentido práctico. Destaca en finanzas, bienes raíces, agricultura, gastronomía, artes, música y cualquier campo que involucre belleza y calidad material. Su capacidad para trabajar consistentemente hacia objetivos a largo plazo es excepcional.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Con el dinero, Tauro es conservador y práctico. Valora la seguridad financiera y trabaja arduamente para construir patrimonio. Le gusta disfrutar de los frutos de su trabajo, pero siempre con un colchón de ahorro. Excelente para administrar recursos y hacer crecer inversiones con paciencia.
                </p>
              </Card>
            </section>

            {/* Compatibility */}
            <section>
              <h2 className="text-2xl font-bold mb-6 text-accent">Compatibilidad de Tauro con los Otros Signos Zodiacales</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-2xl font-bold mb-4">💚 Tu Match Perfecto</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-lg mb-2">♍ Virgo y ♑ Capricornio</h4>
                    <p className="text-muted-foreground">
                      Los signos de tierra comparten valores fundamentales: estabilidad, practicidad y construcción a largo plazo. Con Virgo hay comprensión mutua y apoyo en los detalles. Con Capricornio, una unión poderosa basada en objetivos comunes y respeto.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2">♋ Cáncer y ♓ Piscis</h4>
                    <p className="text-muted-foreground">
                      Los signos de agua complementan la naturaleza terrenal de Tauro. Cáncer aporta nutrición emocional y hogar, mientras que Piscis añade romance, imaginación y profundidad espiritual. Combinaciones que fluyen naturalmente.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-2xl font-bold mb-4">⚡ Los Desafíos y Lecciones de Tauro</h3>
                <div>
                  <h4 className="font-bold text-lg mb-2">♏ Especialmente con Escorpio</h4>
                  <p className="text-muted-foreground mb-4">
                    La relación con Escorpio (su signo opuesto) presenta la mayor tensión creativa. Ambos son signos fijos, lo que genera batallas de voluntades épicas. Sin embargo, cuando funcionan, crean una unión de intensidad y lealtad incomparables.
                  </p>
                  <p className="text-muted-foreground">
                    Los signos de fuego (Aries, Leo, Sagitario) desafían la necesidad de estabilidad de Tauro, mientras que los signos de aire (Géminis, Libra, Acuario) pueden frustrarlo con su naturaleza cambiante. Estas relaciones enseñan a Tauro la lección crucial de la flexibilidad y el desapego.
                  </p>
                </div>
              </Card>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default HoroscopoTauro;